import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next'
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';

// Components
import { BackdropLoading } from '../../components/BackdropLoading';

// Router
import Router from '../../router';

// Services
import { SecurityApi } from '../../services/SecurityApi';
import { UserApi } from '../../services/UserApi';

// Styles
import { Content, BoxModal, ModalContainer, ModalHeader, ModalBody, ModalFooter } from './styles';

// Utils
import { Auth } from '../../utils/auth';
import { loginRequest } from '../../utils/msal/authConfig'
import { callMsGraph } from '../../utils/msal/graph'
import { callPermissionsRole } from '../../utils/commonFunctions';
import { FormButton } from '../styles';


export const HomeSigninAzure = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = React.useState(false);
    const [openRoleModal, setOpenRoleModal] = React.useState(false);
    const [selectedRoleId, setSelectedRoleId] = React.useState('');
    const [availableRoles, setAvailableRoles] = React.useState([]);
    const [roleModalError, setRoleModalError] = React.useState('');

    const adUsernameRef = React.useRef('');
    const adTokenRef = React.useRef('');

    const { instance, accounts } = useMsal();
    const locHash = React.useRef(null);

    const cleanupAD = React.useCallback(async () => {
        localStorage.removeItem('loginInProgress');
        localStorage.removeItem('token');
        localStorage.removeItem('profile');
        localStorage.removeItem('typeLog');
        await instance.clearCache();
    }, [instance]);

    const handleSecondLoginAD = React.useCallback(async (username, accessToken, roleId) => {
        const { authenticate, saveProfile } = new Auth();
        const securityApi = new SecurityApi();
        setLoading(true);
        const resp = await securityApi.loginAD({ username, token: accessToken, roleId });
        setLoading(false);

        if (resp?.status === 200) {
            const token = resp.data.data.accessToken;
            authenticate(token);
            saveProfile(resp.data.data.profile, resp.data.data.strings);
            localStorage.setItem('typeLog', 'tenant');
            await callPermissionsRole(resp.data.data.profile.Role.Id);
            localStorage.removeItem('loginInProgress');
            if (location?.state?.redirect) {
                navigate(location?.state?.redirect, { state: location?.state?.data });
                return;
            }
            navigate(Router.appMyWork);
        } else {
            const errMessage =
                resp?.response?.data?.description ||
                resp?.response?.data?.data?.description ||
                t('login.genericLoginError');
            toast.error(errMessage);
            await cleanupAD();
            navigate(Router.appLogin);
        }
    }, [navigate, location, t, cleanupAD]);

    const handleCancelRoleModal = React.useCallback(async () => {
        setOpenRoleModal(false);
        setSelectedRoleId('');
        setRoleModalError('');
        setAvailableRoles([]);
        await cleanupAD();
        navigate(Router.appLogin);
    }, [cleanupAD, navigate]);

    const handleContinueRoleModal = async () => {
        if (!selectedRoleId) {
            setRoleModalError(t('password.validations.role'));
            return;
        }
        setRoleModalError('');
        setOpenRoleModal(false);
        await handleSecondLoginAD(adUsernameRef.current, adTokenRef.current, selectedRoleId);
    };

    const handleSubmit = React.useCallback(async (responseToken, accessToken) => {
        const { authenticate } = new Auth();
        const userLogin = localStorage.getItem('userLogin');

        if (responseToken?.userPrincipalName?.toString() !== userLogin?.toString()) {
            toast.error(t('password.validations.invalidCredentials'));
            await cleanupAD();
            navigate(Router.appLogin);
            return;
        }

        localStorage.removeItem('userLogin');
        setLoading(true);

        // Primer loginAD SIN roleId
        const securityApi = new SecurityApi();
        const firstResp = await securityApi.loginAD({ username: userLogin, token: accessToken });
        setLoading(false);

        if (firstResp?.status !== 200) {
            const errMessage =
                firstResp?.response?.data?.description ||
                firstResp?.response?.data?.data?.description ||
                t('login.genericLoginError');
            toast.error(errMessage);
            await cleanupAD();
            navigate(Router.appLogin);
            return;
        }

        // Autenticar token temporal — UserApi debe instanciarse DESPUÉS para leer el token correcto
        authenticate(firstResp.data.data.accessToken);
        localStorage.setItem('loginInProgress', '1');
        const userApi = new UserApi();

        setLoading(true);
        const rolesResp = await userApi.rolesOfUser();
        setLoading(false);

        if (rolesResp?.status !== 200) {
            toast.error(t('login.genericLoginError'));
            await cleanupAD();
            navigate(Router.appLogin);
            return;
        }

        const roles = rolesResp.data;

        if (!roles?.length) {
            toast.error(t('login.userNotFound'));
            await cleanupAD();
            navigate(Router.appLogin);
            return;
        }

        adUsernameRef.current = userLogin;
        adTokenRef.current = accessToken;

        if (roles.length === 1) {
            await handleSecondLoginAD(userLogin, accessToken, roles[0].id);
        } else {
            setAvailableRoles(roles);
            setOpenRoleModal(true);
        }
    }, [navigate, t, cleanupAD, handleSecondLoginAD]);

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    useEffect(() => {
        let isMounted = true;

        const requestProfileData = async() => {
            setLoading(true)
            try {
                await delay(8000); // Espera 8 segundos

                if (!isMounted) return;

                if (!accounts?.length) {
                    navigate(Router.appLogin)
                    return
                }
                else
                {
                    const hashRedirect = locHash.current

                    const fragmentParams = new URLSearchParams(hashRedirect.slice(1));
                    const client_info = fragmentParams.get('client_info');

                    const decodeBase64Url = (str) => {
                        const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
                        const decoded = atob(base64);
                        return decodeURIComponent(escape(decoded));
                    };
                    const decodeClientInfo = JSON.parse(decodeBase64Url(client_info));

                    let acc = accounts.filter(x => x.localAccountId === decodeClientInfo.uid)
                    if (acc.length === 0) {
                        toast.error(t('password.validations.invalidCredentials'))
                        navigate(Router.appLogin)
                        return
                    }

                    await instance.acquireTokenSilent({
                        ...loginRequest,
                        account: acc[0],
                    }).then(async(resp) => {
                        setLoading(false)
                        let token = resp?.accessToken ?? ''
                        callMsGraph(token).then((response) => {
                            handleSubmit(response, token)
                        });
                    })
                }
            } catch (error) {
                // Manejar errores si la obtención del token falla
                console.error("Error al obtener el token de sesión:", error);
                setLoading(false)
                toast.error(t('login.loginFailed'))
                navigate(Router.appLogin)
                return
            }
        }
        requestProfileData()

        return () => {
            isMounted = false;
        };
    }, [accounts, navigate, setLoading, instance, handleSubmit, location])

    useEffect(() => {
        if (location.hash) {
            locHash.current = location.hash
        }
    }, [location.hash])

    return (
        <Content container>
            <BackdropLoading open={loading} />

            <Modal
                open={openRoleModal}
                disableEscapeKeyDown
                onClose={(_, reason) => { if (reason === 'backdropClick') return; }} 
            >
                <BoxModal>
                    <ModalContainer>
                        <ModalHeader>
                            {t('login.roleSelectionTitle')}
                        </ModalHeader>
                        <ModalBody>
                            <Typography sx={{ mb: 2 }}>{t('login.roleSelectionSubtitle')}</Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('login.role')}</InputLabel>
                                <Select
                                    value={selectedRoleId}
                                    label={t('login.role')}
                                    onChange={(e) => setSelectedRoleId(e.target.value)}
                                >
                                    {availableRoles.map(role => (
                                        <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {roleModalError && (
                                <Typography color="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
                                    {roleModalError}
                                </Typography>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <FormButton variant="outlined" type="close" onClick={handleCancelRoleModal} disabled={loading}>
                                {t('login.cancel')}
                            </FormButton>
                            <FormButton variant="contained" onClick={handleContinueRoleModal} disabled={loading}>
                                {t('login.continue')}
                            </FormButton>
                        </ModalFooter>
                    </ModalContainer>
                </BoxModal>
            </Modal>
        </Content>
    )
}
